import { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { useVeil } from '../lib/useVeil'
import { maskName } from '../lib/security'
import type { Person, Media } from '../lib/types'

interface Entry {
  date: string
  year: string
  icon: string
  title: string
  detail: string
  kind: 'birth' | 'death' | 'burial'
}

/** Chronicle — the family timeline woven from every recorded life event, with memories attached. */
export default function ChronicleTab({ treeId, persons, onSelect }: { treeId: string; persons: Person[]; onSelect: (p: Person) => void }) {
  const [mediaByPerson, setMediaByPerson] = useState<Map<string, Media[]>>(new Map())
  const veil = useVeil()

  useEffect(() => {
    api.listMedia(treeId).then(all => {
      const m = new Map<string, Media[]>()
      for (const med of all) {
        if (!med.person_id || med.kind === 'avatar' || med.enc) continue
        m.set(med.person_id, [...(m.get(med.person_id) || []), med])
      }
      setMediaByPerson(m)
    }).catch(() => {})
  }, [treeId])
  const entries = useMemo(() => {
    const list: (Entry & { person: Person })[] = []
    for (const p of persons) {
      const name = maskName(p, veil)
      if (p.birth_date) {
        list.push({
          date: p.birth_date, year: p.birth_date.slice(0, 4), icon: '🌅',
          title: `${name} is born`, detail: p.birth_place || '', kind: 'birth', person: p,
        })
      }
      if (p.death_date) {
        list.push({
          date: p.death_date, year: p.death_date.slice(0, 4), icon: '🕯️',
          title: `${name} passes`, detail: p.death_place || '', kind: 'death', person: p,
        })
      }
      if (p.burial_cemetery) {
        list.push({
          date: p.death_date || '9999-12-31', year: p.death_date?.slice(0, 4) || '—', icon: '🕊️',
          title: `${name} — Path to Paradise`,
          detail: [p.burial_cemetery, p.burial_plot ? `Lot ${p.burial_plot}` : null, p.burial_place].filter(Boolean).join(' · '),
          kind: 'burial', person: p,
        })
      }
    }
    return list.sort((a, b) => a.date.localeCompare(b.date))
  }, [persons, veil])

  if (entries.length === 0) {
    return (
      <div className="card-glass p-12 text-center text-slate-500 text-sm">
        <div className="text-4xl mb-3">📜</div>
        The chronicle is unwritten — add birth and passing dates to weave the timeline.
      </div>
    )
  }

  let lastYear = ''
  return (
    <div className="max-w-2xl mx-auto py-2">
      <div className="relative pl-8">
        <div className="absolute left-2.5 top-2 bottom-2 w-px bg-gradient-to-b from-gold-500/60 via-rootv-500/40 to-transparent" />
        {entries.map((e, i) => {
          const showYear = e.year !== lastYear
          lastYear = e.year
          return (
            <div key={i}>
              {showYear && (
                <div className="relative -ml-8 flex items-center gap-3 mt-6 mb-3 first:mt-0">
                  <span className="font-display text-2xl font-bold text-gradient-gold">{e.year}</span>
                  <span className="flex-1 h-px bg-white/[0.06]" />
                </div>
              )}
              <button onClick={() => onSelect(e.person)}
                className="relative w-full text-left mb-2.5 card-glass px-4 py-3 hover:bg-white/[0.07] transition-colors">
                <span className="absolute -left-[30px] top-4 w-3 h-3 rounded-full border-2 border-void-950"
                  style={{ backgroundColor: e.kind === 'birth' ? '#e8bc6a' : e.kind === 'death' ? '#a78bfa' : '#94a3b8' }} />
                <div className="flex items-start gap-3">
                  <span className="text-xl">{e.icon}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-100 font-medium">{e.title}</p>
                    {e.detail && <p className="text-xs text-slate-500 mt-0.5">{e.detail}</p>}
                    {e.date !== '9999-12-31' && <p className="text-[10px] text-slate-600 mt-0.5">{e.kind === 'birth' && e.person.is_living ? e.date.slice(0, 4) : e.date}</p>}
                    {e.kind === (e.person.birth_date ? 'birth' : e.person.death_date ? 'death' : 'burial') && (mediaByPerson.get(e.person.id)?.length || 0) > 0 && (
                      <div className="flex gap-1.5 mt-2">
                        {mediaByPerson.get(e.person.id)!.slice(0, 3).map(m => (
                          m.kind === 'video'
                            ? <video key={m.id} src={api.mediaUrl(m.id)} className="w-12 h-12 rounded-lg object-cover border border-white/10" muted />
                            : m.kind === 'document'
                              ? <span key={m.id} className="w-12 h-12 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-lg">📄</span>
                              : <img key={m.id} src={api.mediaUrl(m.id)} alt="" className="w-12 h-12 rounded-lg object-cover border border-white/10" loading="lazy" />
                        ))}
                        {mediaByPerson.get(e.person.id)!.length > 3 && (
                          <span className="w-12 h-12 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-[10px] text-slate-400">
                            +{mediaByPerson.get(e.person.id)!.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
